import React, { createContext, useContext } from 'react';
import {
    ServerRequestState,
    useServerFetch,
} from 'dg-web-shared/lib/hooks/ServerStateHooks';
import { AuthStatusType, useAuthState } from '../../../app/state/AuthState';

type LoginsContextValue = {
    state: ServerRequestState<GetLoginData[], null>;
    refresh: () => void;
};

const LoginsContext = createContext<LoginsContextValue | null>(null);

export function LoginsProvider(props: { children: React.ReactNode }) {
    const { authStatus } = useAuthState();
    const shouldFetch = authStatus === AuthStatusType.OK;

    const [fetchState, refetch] = useServerFetch<
        GetLoginData[],
        Record<string, never>,
        null
    >(
        () => ({ url: '/ui-api/customer-account/logins-v2' }),
        shouldFetch ? {} : null,
    );

    const loginsContextValue = React.useMemo(
        () => ({ state: fetchState, refresh: refetch }),
        [fetchState, refetch],
    );

    return (
        <LoginsContext.Provider value={loginsContextValue}>
            {props.children}
        </LoginsContext.Provider>
    );
}

export function useLoginsState(): ServerRequestState<GetLoginData[], null> {
    const context = useContext(LoginsContext);
    if (!context) {
        throw new Error('useLoginsState must be used within LoginsProvider');
    }
    return context.state;
}

export function useLoginsRefetch(): () => void {
    const context = useContext(LoginsContext);
    if (!context) {
        throw new Error('useLoginsRefetch must be used within LoginsProvider');
    }
    return context.refresh;
}

export type GetLoginData = {
    id: number;
    confirmed: boolean;
    email: string;
    requestedEmail: string | null;
    roleId: number;
    firstName: string | null;
    lastName: string | null;
    deactivated: boolean;
    gender: 'm' | 'f' | null;
};
